import type {
    AgreementStatus,
    ComplianceStatus,
    Criticality,
    CsfFunction,
    Direction,
    FindingStatus,
    InitiativeStatus,
    Period,
    Rag,
    RiskStatus
} from '@/lib/data/types'

/* ------------------------------------------------------------------ */
/* Text normalization                                                  */
/* ------------------------------------------------------------------ */

const STOP_WORDS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'y', 'e', 'en', 'por', 'para', 'con', 'al', 'the', 'of', 'a', 'an', 'and', 'to', 'in', 'for'])

// Character classes are built from code points so the source stays plain ASCII
const charRange = (from: number, to: number): string => `${String.fromCharCode(from)}-${String.fromCharCode(to)}`

/** Combining accents left behind by NFD normalization. */
const DIACRITICS = new RegExp(`[${charRange(0x0300, 0x036f)}]`, 'g')

/** Control characters, zero-width characters and bidirectional overrides. */
const INVISIBLE = new RegExp(`[${[
    charRange(0x00, 0x08),
    charRange(0x0b, 0x0c),
    charRange(0x0e, 0x1f),
    charRange(0x7f, 0x7f),
    charRange(0x200b, 0x200f),
    charRange(0x202a, 0x202e),
    charRange(0x2066, 0x2069),
    charRange(0xfeff, 0xfeff)
].join('')}]`, 'g')

/** Lowercase, no accents, alphanumerics separated by single spaces. */
export const normalizeValue = (input: unknown): string => {
    if (input === null || input === undefined) return ''

    return String(input)
        .normalize('NFD')
        .replace(DIACRITICS, '')
        .toLowerCase()
        .replace(/%/g, ' pct ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
}

const stem = (token: string): string => {
    if (token.length > 4 && token.endsWith('es') && /[lrndszj]es$/.test(token)) return token.slice(0, -2)
    if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1)

    return token
}

/** Token list used to compare headers and sheet names with synonyms. */
export const headerTokens = (input: unknown): string[] => {
    return normalizeValue(input)
        .split(' ')
        .filter(token => token && !STOP_WORDS.has(token))
        .map(stem)
}

export interface Tokens {
    list: string[]
    key: string
}

export const tokenize = (input: unknown): Tokens => {
    const list = headerTokens(input)

    return { list, key: list.join(' ') }
}

/**
 * Similarity between a header and a synonym. Exact token match wins, then a
 * header that contains every token of the synonym, penalized by extra words.
 */
export const matchScore = (header: Tokens, synonym: Tokens): number => {
    if (!header.list.length || !synonym.list.length) return 0

    // Longer synonyms are more specific: they win ties
    const specificity = Math.min(synonym.key.length, 60) / 1000

    if (header.key === synonym.key) return 100 + synonym.list.length + specificity

    for (const token of synonym.list) {
        if (!header.list.includes(token)) return 0
    }

    return 50 + synonym.list.length * 10 - (header.list.length - synonym.list.length) + specificity
}

/* ------------------------------------------------------------------ */
/* Cell sanitizing                                                     */
/* ------------------------------------------------------------------ */

const MAX_TEXT = 400

/** Plain text, no control or invisible characters, trimmed and length-capped. */
export const cleanText = (input: unknown): string => {
    if (input === null || input === undefined) return ''
    if (typeof input === 'number' && !Number.isFinite(input)) return ''

    const text = String(input)
        .replace(INVISIBLE, '')
        .replace(/\s+/g, ' ')
        .trim()

    return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT - 1)}…` : text
}

/** Parses numbers written as 1,234.56 or 1.234,56, with currency or % signs. */
export const parseNumber = (input: unknown): number | null => {
    if (input === null || input === undefined || input === '') return null
    if (typeof input === 'number') return Number.isFinite(input) ? input : null
    if (typeof input === 'boolean') return input ? 1 : 0

    let text = String(input).trim()

    if (!text) return null

    const negative = /^\(.*\)$/.test(text) || /^-/.test(text) || /-$/.test(text)

    text = text.replace(/[^0-9.,]/g, '')

    if (!text || !/[0-9]/.test(text)) return null

    const lastDot = text.lastIndexOf('.')
    const lastComma = text.lastIndexOf(',')

    if (lastDot >= 0 && lastComma >= 0) {
        if (lastComma > lastDot) text = text.replace(/\./g, '').replace(',', '.')
        else text = text.replace(/,/g, '')
    }
    else if (lastComma >= 0) {
        const parts = text.split(',')

        if (parts.length === 2 && parts[1].length !== 3) text = `${parts[0]}.${parts[1]}`
        else text = parts.join('')
    }
    else if (lastDot >= 0) {
        const parts = text.split('.')

        if (parts.length > 2) text = parts.join('')
    }

    const value = Number(text)

    if (!Number.isFinite(value)) return null

    return negative ? -value : value
}

export const isPercentText = (input: unknown): boolean => typeof input === 'string' && input.includes('%')

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

export const DAY = 86400000

export const utc = (year: number, month: number, day: number): number => Date.UTC(year, month, day)

const MONTHS: Record<string, number> = {
    ene: 0, enero: 0, jan: 0, january: 0,
    feb: 1, febrero: 1, february: 1,
    mar: 2, marzo: 2, march: 2,
    abr: 3, abril: 3, apr: 3, april: 3,
    may: 4, mayo: 4,
    jun: 5, junio: 5, june: 5,
    jul: 6, julio: 6, july: 6,
    ago: 7, agosto: 7, aug: 7, august: 7,
    sep: 8, sept: 8, septiembre: 8, setiembre: 8, september: 8,
    oct: 9, octubre: 9, october: 9,
    nov: 10, noviembre: 10, november: 10,
    dic: 11, diciembre: 11, dec: 11, december: 11
}

const validDate = (year: number, month: number, day: number): number | null => {
    if (year < 1950 || year > 2200 || month < 0 || month > 11 || day < 1 || day > 31) return null

    const ts = utc(year, month, day)
    const check = new Date(ts)

    if (check.getUTCMonth() !== month) return null

    return ts
}

const fullYear = (year: number): number => (year < 100 ? 2000 + year : year)

/** Excel serial (1900 or 1904 system) or text date to a UTC midnight timestamp. */
export const parseDate = (input: unknown, date1904 = false): number | null => {
    if (input === null || input === undefined || input === '') return null

    if (typeof input === 'number') {
        if (!Number.isFinite(input) || input < 1 || input > 2958465) return null

        const serial = Math.floor(input) + (date1904 ? 1462 : 0)
        const ts = (serial - 25569) * DAY
        const year = new Date(ts).getUTCFullYear()

        return year >= 1950 && year <= 2200 ? ts : null
    }

    if (input instanceof Date) {
        if (Number.isNaN(input.getTime())) return null

        return utc(input.getFullYear(), input.getMonth(), input.getDate())
    }

    const iso = String(input).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/)

    if (iso) return validDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))

    const text = normalizeValue(input)

    if (!text) return null

    const parts = text.split(' ')
    const numbers = parts.map(part => (/^\d+$/.test(part) ? Number(part) : null))

    // yyyy mm dd
    if (parts.length >= 3 && numbers[0] !== null && parts[0].length === 4 && numbers[1] !== null && numbers[2] !== null) {
        return validDate(numbers[0] as number, (numbers[1] as number) - 1, numbers[2] as number)
    }

    // dd mm yyyy (Mexico) or mm dd yyyy when the first part cannot be a day
    if (parts.length >= 3 && numbers[0] !== null && numbers[1] !== null && numbers[2] !== null) {
        const a = numbers[0] as number
        const b = numbers[1] as number
        const year = fullYear(numbers[2] as number)

        if (a > 12 && b <= 12) return validDate(year, b - 1, a)
        if (b > 12 && a <= 12) return validDate(year, a - 1, b)

        return validDate(year, b - 1, a)
    }

    // dd mon yyyy  /  mon dd yyyy  /  dd de mes de yyyy
    const monthIndex = parts.findIndex(part => part in MONTHS)

    if (monthIndex >= 0) {
        const month = MONTHS[parts[monthIndex]]
        const others = parts.filter((_, index) => index !== monthIndex).filter(part => /^\d+$/.test(part)).map(Number)
        let year = others.find(value => value > 31)
        let day = others.find(value => value <= 31)

        // 15-mar-26: two small numbers, the last one is the year
        if (year === undefined && others.length >= 2) {
            day = others[0]
            year = others[others.length - 1]
        }

        if (year !== undefined && day !== undefined) return validDate(fullYear(year), month, day)
    }

    return null
}

export const startOfMonth = (ts: number): number => {
    const date = new Date(ts)

    return utc(date.getUTCFullYear(), date.getUTCMonth(), 1)
}

export const endOfMonth = (ts: number): number => {
    const date = new Date(ts)

    return utc(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
}

const pad = (value: number): string => String(value).padStart(2, '0')

export const monthKey = (ts: number): string => {
    const date = new Date(ts)

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`
}

export const quarterOf = (ts: number): { year: number, quarter: number } => {
    const date = new Date(ts)

    return { year: date.getUTCFullYear(), quarter: Math.floor(date.getUTCMonth() / 3) + 1 }
}

const monthPeriod = (year: number, month: number, raw: string): Period => {
    const start = utc(year, month, 1)

    return { kind: 'month', start, end: endOfMonth(start), key: monthKey(start), raw }
}

const quarterPeriod = (year: number, quarter: number, raw: string): Period => {
    const start = utc(year, (quarter - 1) * 3, 1)

    return { kind: 'quarter', start, end: utc(year, quarter * 3, 0), key: `${year}-Q${quarter}`, raw }
}

/** Periods: dates, months (2026-03, mar 2026, 03/2026), quarters (T1 2026, Q1-26), years. */
export const parsePeriod = (input: unknown, date1904 = false): Period | null => {
    if (input === null || input === undefined || input === '') return null

    const raw = cleanText(input)

    if (typeof input === 'number') {
        if (input >= 1900 && input <= 2200 && Number.isInteger(input)) {
            return { kind: 'year', start: utc(input, 0, 1), end: utc(input, 11, 31), key: String(input), raw }
        }

        const ts = parseDate(input, date1904)

        if (ts === null) return null

        const date = new Date(ts)

        if (date.getUTCDate() === 1) return monthPeriod(date.getUTCFullYear(), date.getUTCMonth(), raw)

        return { kind: 'day', start: ts, end: ts, key: new Date(ts).toISOString().slice(0, 10), raw }
    }

    const text = normalizeValue(input)

    if (!text) return null

    const parts = text.split(' ')

    // Quarters: t1 2026, q1 2026, 2026 q1, 1t 2026, 2026 t1, 1t26
    const quarterMatch = text.match(/^(?:([tq])\s?([1-4])|([1-4])\s?([tq]))\s?(\d{2,4})$/) || text.match(/^(\d{4})\s?([tq])\s?([1-4])$/)

    if (quarterMatch) {
        if (quarterMatch.length === 6) {
            const quarter = Number(quarterMatch[2] || quarterMatch[3])
            const year = fullYear(Number(quarterMatch[5]))

            return quarterPeriod(year, quarter, raw)
        }

        return quarterPeriod(Number(quarterMatch[1]), Number(quarterMatch[3]), raw)
    }

    // Year only
    if (/^\d{4}$/.test(text)) {
        const year = Number(text)

        if (year >= 1950 && year <= 2200) return { kind: 'year', start: utc(year, 0, 1), end: utc(year, 11, 31), key: text, raw }
    }

    // yyyy mm  /  mm yyyy
    if (parts.length === 2 && parts.every(part => /^\d+$/.test(part))) {
        const [a, b] = parts.map(Number)

        if (parts[0].length === 4 && b >= 1 && b <= 12) return monthPeriod(a, b - 1, raw)
        if (parts[1].length === 4 && a >= 1 && a <= 12) return monthPeriod(b, a - 1, raw)
        if (parts[1].length === 2 && a >= 1 && a <= 12) return monthPeriod(fullYear(b), a - 1, raw)
    }

    // mon yyyy / yyyy mon / mon yy
    if (parts.length === 2) {
        const monthIndex = parts.findIndex(part => part in MONTHS)

        if (monthIndex >= 0) {
            const other = parts[1 - monthIndex]

            if (/^\d{2,4}$/.test(other)) return monthPeriod(fullYear(Number(other)), MONTHS[parts[monthIndex]], raw)
        }
    }

    const ts = parseDate(input, date1904)

    if (ts !== null) {
        const date = new Date(ts)

        if (date.getUTCDate() === 1) return monthPeriod(date.getUTCFullYear(), date.getUTCMonth(), raw)

        return { kind: 'day', start: ts, end: ts, key: new Date(ts).toISOString().slice(0, 10), raw }
    }

    return { kind: 'text', start: 0, end: 0, key: `text:${text}`, raw }
}

/* ------------------------------------------------------------------ */
/* Categorical values                                                  */
/* ------------------------------------------------------------------ */

type Dictionary<T extends string> = [T, string[]][]

/** Exact match first, then phrase containment in dictionary order. */
const lookup = <T extends string>(input: unknown, dictionary: Dictionary<T>): T | null => {
    const text = normalizeValue(input)

    if (!text) return null

    for (const [value, words] of dictionary) {
        if (words.includes(text)) return value
    }

    const padded = ` ${text} `

    for (const [value, words] of dictionary) {
        if (words.some(word => word.length > 2 && padded.includes(` ${word} `))) return value
    }

    return null
}

const INITIATIVE_STATUS: Dictionary<InitiativeStatus> = [
    ['cancelled', ['cancelada', 'cancelado', 'descartada', 'descartado', 'cancelled', 'canceled', 'anulada']],
    ['onHold', ['en pausa', 'pausa', 'pausada', 'pausado', 'detenida', 'detenido', 'suspendida', 'suspendido', 'on hold', 'hold', 'paused', 'bloqueada', 'bloqueado', 'blocked', 'en espera']],
    ['closed', ['cerrada', 'cerrado', 'concluida', 'concluido', 'terminada', 'terminado', 'completada', 'completado', 'finalizada', 'finalizado', 'entregada', 'implementada', 'implementado', 'done', 'closed', 'completed', 'complete', 'finished', 'cumplido', 'cumplida']],
    ['backlog', ['backlog', 'por iniciar', 'no iniciada', 'no iniciado', 'sin iniciar', 'planeada', 'planeado', 'pendiente', 'en planeacion', 'not started', 'planned', 'to do', 'todo', 'nuevo', 'nueva', 'propuesta', 'idea']],
    ['active', ['en curso', 'en proceso', 'activa', 'activo', 'en ejecucion', 'ejecucion', 'in progress', 'active', 'ongoing', 'abierta', 'abierto', 'en desarrollo', 'en implementacion', 'iniciada', 'iniciado', 'en riesgo', 'retrasada', 'retrasado', 'atrasada', 'atrasado', 'wip', 'doing', 'vigente']]
]

const COMPLIANCE_STATUS: Dictionary<ComplianceStatus> = [
    ['notApplicable', ['no aplica', 'n a', 'na', 'not applicable']],
    ['nonCompliant', ['no cumple', 'incumplido', 'incumplimiento', 'no conforme', 'non compliant', 'noncompliant', 'gap', 'brecha', 'no cumplido']],
    ['compliant', ['cumplido', 'cumplida', 'cumple', 'implementado', 'implementada', 'cerrado', 'cerrada', 'completado', 'completada', 'atendido', 'atendida', 'compliant', 'done', 'closed', 'cubierto', 'conforme']],
    ['pending', ['pendiente', 'no iniciado', 'por iniciar', 'sin iniciar', 'not started', 'pending', 'abierto', 'abierta', 'nuevo', 'planeado']],
    ['inProgress', ['en proceso', 'en curso', 'parcial', 'parcialmente', 'en implementacion', 'in progress', 'partial', 'en ejecucion', 'en remediacion', 'en atencion']]
]

const FINDING_STATUS: Dictionary<FindingStatus> = [
    ['accepted', ['riesgo aceptado', 'aceptado', 'aceptada', 'accepted', 'risk accepted', 'excepcion', 'exception']],
    ['closed', ['cerrado', 'cerrada', 'remediado', 'remediada', 'resuelto', 'resuelta', 'corregido', 'corregida', 'mitigado', 'mitigada', 'closed', 'fixed', 'resolved', 'remediated', 'atendido', 'atendida', 'falso positivo', 'false positive']],
    ['inRemediation', ['en remediacion', 'remediacion', 'en proceso', 'en curso', 'en atencion', 'in progress', 'in remediation', 'mitigando', 'en tratamiento', 'en correccion']],
    ['open', ['abierto', 'abierta', 'nuevo', 'nueva', 'open', 'new', 'pendiente', 'identificado', 'identificada', 'reportado', 'reportada', 'reabierto']]
]

const RISK_STATUS: Dictionary<RiskStatus> = [
    ['closed', ['cerrado', 'cerrada', 'closed', 'retirado', 'retirada']],
    ['mitigated', ['mitigado', 'mitigada', 'mitigated', 'controlado', 'controlada', 'reducido', 'transferido', 'transferida', 'transferred']],
    ['accepted', ['aceptado', 'aceptada', 'accepted', 'asumido', 'tolerado']],
    ['treating', ['en tratamiento', 'tratamiento', 'en mitigacion', 'mitigando', 'en proceso', 'in treatment', 'treating', 'en curso', 'plan de accion']],
    ['open', ['abierto', 'abierta', 'open', 'identificado', 'identificada', 'nuevo', 'activo', 'vigente', 'pendiente']]
]

const AGREEMENT_STATUS: Dictionary<AgreementStatus> = [
    ['cancelled', ['cancelado', 'cancelada', 'cancelled', 'canceled', 'descartado', 'sin efecto']],
    ['done', ['cumplido', 'cumplida', 'cerrado', 'cerrada', 'atendido', 'atendida', 'completado', 'completada', 'concluido', 'done', 'closed', 'completed', 'realizado', 'resuelto', 'entregado']],
    ['inProgress', ['en proceso', 'en curso', 'en atencion', 'in progress', 'en seguimiento', 'parcial', 'en ejecucion']],
    ['pending', ['pendiente', 'abierto', 'abierta', 'por iniciar', 'nuevo', 'pending', 'open', 'sin iniciar', 'vencido', 'vencida', 'atrasado', 'atrasada']]
]

const CRITICALITY: Dictionary<Criticality> = [
    ['critical', ['critica', 'critico', 'critical', 'muy alta', 'muy alto', 'urgente', 'p1', '1', 'severa', 'severo', 'extrema', 'extremo', 'very high', 'p0']],
    ['low', ['baja', 'bajo', 'low', 'p4', '4', 'menor', 'informativa', 'informativo', 'info', 'informational', 'minima', 'very low', 'muy baja', 'muy bajo']],
    ['high', ['alta', 'alto', 'high', 'p2', '2', 'importante', 'mayor']],
    ['medium', ['media', 'medio', 'medium', 'moderada', 'moderado', 'p3', '3', 'normal', 'moderate']]
]

const RAG: Dictionary<Rag> = [
    ['red', ['rojo', 'roja', 'red', 'r', 'fuera de plan', 'off track', 'critico', 'retrasado', 'atrasado', 'problema']],
    ['amber', ['ambar', 'amarillo', 'amarilla', 'amber', 'yellow', 'a', 'y', 'en riesgo', 'at risk', 'atencion', 'precaucion', 'naranja']],
    ['green', ['verde', 'green', 'g', 'en tiempo', 'on track', 'ok', 'bien', 'en plan', 'controlado', 'en control']]
]

const CSF: Dictionary<CsfFunction> = [
    ['GV', ['gv', 'gobernar', 'gobierno', 'govern', 'governance', 'gobernanza']],
    ['ID', ['id', 'identificar', 'identify', 'identificacion']],
    ['PR', ['pr', 'proteger', 'protect', 'proteccion']],
    ['DE', ['de', 'detectar', 'detect', 'deteccion']],
    ['RS', ['rs', 'responder', 'respond', 'respuesta']],
    ['RC', ['rc', 'recuperar', 'recover', 'recuperacion']]
]

const DIRECTIONS: Dictionary<'up' | 'down'> = [
    ['down', ['menor es mejor', 'menor', 'descendente', 'abajo', 'minimizar', 'lower is better', 'lower', 'down', 'min', 'negativo', 'decreciente', 'reducir', 'bajar']],
    ['up', ['mayor es mejor', 'mayor', 'ascendente', 'arriba', 'maximizar', 'higher is better', 'higher', 'up', 'max', 'positivo', 'creciente', 'aumentar', 'subir']]
]

export const toInitiativeStatus = (input: unknown): InitiativeStatus | null => lookup(input, INITIATIVE_STATUS)
export const toComplianceStatus = (input: unknown): ComplianceStatus | null => lookup(input, COMPLIANCE_STATUS)
export const toFindingStatus = (input: unknown): FindingStatus | null => lookup(input, FINDING_STATUS)
export const toRiskStatus = (input: unknown): RiskStatus | null => lookup(input, RISK_STATUS)
export const toAgreementStatus = (input: unknown): AgreementStatus | null => lookup(input, AGREEMENT_STATUS)
export const toCriticality = (input: unknown): Criticality | null => lookup(input, CRITICALITY)
export const toRag = (input: unknown): Rag | null => lookup(input, RAG)

export const toDirection = (input: unknown): Direction => {
    const raw = cleanText(input)

    if (raw === '+' || raw === String.fromCharCode(0x2191)) return 'up'
    if (raw === '-' || raw === String.fromCharCode(0x2193)) return 'down'

    return lookup(input, DIRECTIONS)
}

/** CSF function from its own column or from the category prefix (ID.AM). */
export const toCsfFunction = (fnInput: unknown, categoryInput: unknown): CsfFunction | null => {
    const direct = lookup(fnInput, CSF)

    if (direct) return direct

    const prefix = normalizeValue(categoryInput).split(' ')[0]

    if (!prefix) return null

    return lookup(prefix, CSF)
}

/** Tier 1, T1, Nivel 2, Tier III, 3 -> 1..4 */
export const toTier = (input: unknown): number | null => {
    if (typeof input === 'number') return input >= 0 && input <= 5 ? Math.round(input) : null

    const text = normalizeValue(input)

    if (!text) return null

    const digit = text.match(/(\d+)/)

    if (digit) {
        const value = Number(digit[1])

        return value >= 0 && value <= 5 ? value : null
    }

    const roman: Record<string, number> = { i: 1, ii: 2, iii: 3, iv: 4, v: 5 }
    const token = text.split(' ').find(part => part in roman)

    return token ? roman[token] : null
}

export const toCurrency = (input: unknown): string | null => {
    const text = normalizeValue(input)

    if (!text) return null
    if (['mxn', 'mx', 'pesos', 'peso', 'mn', 'm n', 'mxp', 'pesos mexicanos'].includes(text)) return 'MXN'
    if (['usd', 'us', 'dolares', 'dolar', 'dlls', 'dll', 'us dollar', 'dollars', 'dollar', 'dolares americanos'].includes(text)) return 'USD'
    if (/^[a-z]{3}$/.test(text)) return text.toUpperCase()

    return null
}

/** Stable key for joining rows by id, or by name when there is no id. */
export const joinKey = (id: string, name: string): string => {
    const byId = normalizeValue(id)

    if (byId) return `id:${byId}`

    const byName = normalizeValue(name)

    return byName ? `name:${byName}` : ''
}

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))
